/**
 * Nova oportunidade (F1-50; pacote 3 · P3-08, P3-09 e D2): o MESMO modal na listagem geral, no Kanban e na aba
 * Oportunidades do Cliente.
 *  - Cliente por busca no servidor (nome, razão social, código ou CNPJ), com limite de resultados — nada de milhares
 *    de opções para rolar; seleção explícita de um Cliente.
 *  - Filial/unidade atendida em destaque (nome, código, CNPJ e cidade/UF); com mais de uma unidade, nenhuma vem
 *    escolhida; trocar o Cliente limpa a unidade.
 *  - Próxima ação preenchida por padrão ("Qualificar a oportunidade", hoje, responsável) e editável.
 *  - Confirmação objetiva antes de criar: "Voltar e corrigir" / "Confirmar e criar".
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '../api.ts';
import { navegar } from '../rotas.ts';
import { TIPOS, type Tipo } from '../../../backend/src/compartilhado/oportunidades.ts';
import { Icone } from '../componentes/icones.tsx';
import { hojeIso } from './CampoData.tsx';
import { Ajuda, Modal } from './ui.tsx';
import type { Cliente, ClienteResumo, Entidade } from './tipos.ts';

const LIMITE = 20;
const localEntidade = (e: Pick<Entidade, 'cidade' | 'uf'>) => [e.cidade, e.uf].filter(Boolean).join('/');

/** Bloco da unidade escolhida (P3-09): nome, CNPJ, código e local, em destaque. */
function ResumoUnidade({ e }: { e: Entidade }) {
  return (
    <div className="resumo-unidade" data-teste="resumo-unidade">
      <span>
        CNPJ <b>{e.cnpj}</b>
      </span>
      {e.codigo && (
        <span>
          Código <b>{e.codigo}</b>
        </span>
      )}
      <span>
        Local <b>{localEntidade(e) || '—'}</b>
      </span>
    </div>
  );
}

export function ModalNovaOportunidade({ cliente: fixo, responsaveis, responsavelPadrao, aoFechar }: { cliente?: Cliente; responsaveis: { id: string; nome: string }[]; responsavelPadrao?: string; aoFechar: () => void }) {
  const [busca, setBusca] = useState('');
  const [achados, setAchados] = useState<ClienteResumo[] | null>(null);
  const [haMais, setHaMais] = useState(false);
  const [clienteId, setClienteId] = useState(fixo?.id ?? '');
  const [cliente, setCliente] = useState<Cliente | null>(fixo ?? null);
  const [entidadeId, setEntidadeId] = useState('');
  const [abrirUnidades, setAbrirUnidades] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [tipo, setTipo] = useState<Tipo>('venda');
  const [responsavel, setResponsavel] = useState(fixo?.responsavel_id ?? responsavelPadrao ?? '');
  const [acao, setAcao] = useState({ descricao: 'Qualificar a oportunidade', data: hojeIso(), responsavel_id: '' });
  const [confirmar, setConfirmar] = useState(false);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const pedido = useRef(0);

  // P3-08: busca no servidor com limite (pede LIMITE + 1 para saber se há mais)
  useEffect(() => {
    if (fixo || clienteId) return;
    const termo = busca.trim();
    if (termo.length < 2) {
      setAchados(null);
      setHaMais(false);
      return;
    }
    const n = ++pedido.current;
    const t = setTimeout(() => {
      api.get<ClienteResumo[]>(`/api/clientes?status=ativo&limite=${LIMITE + 1}&busca=${encodeURIComponent(termo)}`).then(
        (l) => {
          if (n !== pedido.current) return;
          setAchados(l.slice(0, LIMITE));
          setHaMais(l.length > LIMITE);
        },
        (e: Error) => setErro(e.message),
      );
    }, 250);
    return () => clearTimeout(t);
  }, [busca, clienteId, fixo]);

  useEffect(() => {
    if (fixo) return;
    if (!clienteId) return void setCliente(null);
    api.get<Cliente>(`/api/clientes/${clienteId}`).then(
      (c) => {
        setCliente(c);
        if (!responsavel) setResponsavel(c.responsavel_id);
      },
      (e: Error) => setErro(e.message),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, fixo]);
  const entidades = (cliente?.entidades ?? []).filter((e) => e.status === 'ativo').sort((a, b) => Number(Boolean(b.principal)) - Number(Boolean(a.principal)));
  // P3-09: com uma única unidade não há o que escolher; com mais de uma, nenhuma vem escolhida
  useEffect(() => setEntidadeId(entidades.length === 1 ? entidades[0].id : ''), [cliente]); // eslint-disable-line react-hooks/exhaustive-deps
  const unidade = entidades.find((e) => e.id === entidadeId);

  const escolherCliente = (c: ClienteResumo) => {
    setClienteId(c.id);
    setBusca(c.nome);
    setAchados(null);
    setEntidadeId('');
  };
  const trocarCliente = () => {
    setClienteId('');
    setCliente(null);
    setEntidadeId('');
    setBusca('');
  };

  const pronto = Boolean(clienteId && entidadeId && titulo.trim() && responsavel && acao.descricao.trim() && acao.data);
  const criar = async () => {
    setErro('');
    setSalvando(true);
    try {
      const o = await api.post<{ id: string }>('/api/oportunidades', {
        cliente_id: clienteId,
        entidade_id: entidadeId,
        titulo: titulo.trim(),
        tipo,
        responsavel_id: responsavel,
        proxima_acao: { descricao: acao.descricao.trim(), data: acao.data, responsavel_id: acao.responsavel_id || responsavel },
      });
      aoFechar();
      navegar(`/oportunidades/${o.id}`);
    } catch (e) {
      setErro((e as Error).message);
      setConfirmar(false);
    } finally {
      setSalvando(false);
    }
  };

  if (confirmar && cliente && unidade)
    return (
      <Modal
        titulo="Confirmar criação"
        aoFechar={aoFechar}
        rodape={
          <>
            <button type="button" className="botao-secundario" onClick={() => setConfirmar(false)} data-teste="voltar-corrigir">
              Voltar e corrigir
            </button>
            <button type="button" className="botao-primario" disabled={salvando} aria-busy={salvando} onClick={criar} data-teste="confirmar-criar">
              Confirmar e criar
            </button>
          </>
        }
      >
        <div className="confirmacao-criacao" data-teste="confirmacao-oportunidade">
          <div className="par-confirmacao">
            <span className="rotulo-campo">Cliente</span>
            <span>
              {cliente.nome} ({cliente.codigo})
            </span>
          </div>
          <div className="bloco-unidade" data-teste="confirmacao-unidade">
            <div className="cab-unidade">
              <Icone nome="clientes" /> Filial/unidade atendida
            </div>
            <strong className="nome-unidade">{unidade.nome_fantasia || unidade.razao_social}</strong>
            <ResumoUnidade e={unidade} />
          </div>
          <div className="pares-confirmacao">
            <div className="par-confirmacao">
              <span className="rotulo-campo">Tipo</span>
              <span>{TIPOS[tipo]}</span>
            </div>
            <div className="par-confirmacao">
              <span className="rotulo-campo">Responsável</span>
              <span>{responsaveis.find((r) => r.id === responsavel)?.nome ?? '—'}</span>
            </div>
          </div>
        </div>
      </Modal>
    );

  return (
    <Modal
      titulo={fixo ? `Nova oportunidade · ${fixo.nome}` : 'Nova oportunidade'}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={!pronto} onClick={() => setConfirmar(true)} data-teste="criar-oportunidade">
            Continuar
          </button>
        </>
      }
    >
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-nova-oportunidade">
          {erro}
        </p>
      )}
      {!fixo && (
        <div className="campo busca-cliente">
          <label htmlFor="busca-cliente-oportunidade">
            Cliente *<Ajuda titulo="Cliente">Busca por nome, razão social, código ou CNPJ. Mostra até 20 resultados: refine a busca para ver outros.</Ajuda>
          </label>
          {clienteId ? (
            <div className="cliente-escolhido" data-teste="cliente-escolhido">
              <span>{cliente ? `${cliente.nome} · ${cliente.codigo}` : busca}</span>
              <button type="button" className="botao-link" onClick={trocarCliente} data-teste="trocar-cliente">
                Trocar
              </button>
            </div>
          ) : (
            <>
              <input id="busca-cliente-oportunidade" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Digite ao menos 2 letras" autoComplete="off" data-teste="cliente-oportunidade" />
              {achados && (
                <ul className="lista-autocompletar" role="listbox" aria-label="Clientes encontrados" data-teste="clientes-encontrados">
                  {achados.map((c) => (
                    <li key={c.id}>
                      <button type="button" role="option" aria-selected={false} onClick={() => escolherCliente(c)} data-teste={`escolher-cliente-${c.codigo}`}>
                        <strong>{c.nome}</strong> · {c.codigo}
                        <small>{[c.matriz?.cnpj, c.matriz ? localEntidade(c.matriz) : '', `${c.unidades_ativas ?? c.entidades ?? 1} unidade(s)`].filter(Boolean).join(' · ')}</small>
                      </button>
                    </li>
                  ))}
                  {achados.length === 0 && <li className="texto-secundario sem-resultado">Nenhum Cliente ativo encontrado.</li>}
                  {haMais && <li className="texto-secundario sem-resultado">Há mais resultados: refine a busca.</li>}
                </ul>
              )}
            </>
          )}
        </div>
      )}
      <div className="bloco-unidade" data-teste="bloco-unidade">
        <div className="cab-unidade" id="rotulo-unidade">
          <Icone nome="clientes" /> Filial/unidade atendida *<Ajuda titulo="Filial/unidade atendida">A unidade atendida não precisa ser a principal. Faturamento e entrega podem ser outras unidades, definidas na Proposta.</Ajuda>
        </div>
        {!cliente ? (
          <div className="seletor-unidade vazio">Escolha antes o Cliente</div>
        ) : (
          <>
            <button type="button" className={`seletor-unidade${unidade ? '' : ' vazio'}`} aria-haspopup="listbox" aria-expanded={abrirUnidades} aria-labelledby="rotulo-unidade" onClick={() => setAbrirUnidades(!abrirUnidades)} data-teste="entidade-oportunidade">
              <span>{unidade ? unidade.nome_fantasia || unidade.razao_social : `Escolha a unidade (${entidades.length} ativa${entidades.length === 1 ? '' : 's'})`}</span>
              <span aria-hidden>▾</span>
            </button>
            {abrirUnidades && (
              <ul className="lista-autocompletar" role="listbox" aria-labelledby="rotulo-unidade" data-teste="lista-unidades">
                {entidades.map((e) => (
                  <li key={e.id}>
                    <button type="button" role="option" aria-selected={e.id === entidadeId} className={e.id === entidadeId ? 'selecionada' : ''} onClick={() => (setEntidadeId(e.id), setAbrirUnidades(false))} data-teste={`escolher-unidade-${e.cnpj}`}>
                      <strong>{e.nome_fantasia || e.razao_social}</strong>
                      {e.principal ? ' · Principal' : ''}
                      <small>{[e.codigo, e.cnpj, localEntidade(e)].filter(Boolean).join(' · ')}</small>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {unidade && !abrirUnidades && <ResumoUnidade e={unidade} />}
          </>
        )}
      </div>
      <div className="form-grade grade-3">
        <label className="campo">
          Título *
          <input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="ex.: Wi-Fi da nova unidade" data-teste="titulo-oportunidade" />
        </label>
        <label className="campo">
          Tipo de negociação *
          <select value={tipo} onChange={(e) => setTipo(e.target.value as Tipo)} data-teste="tipo-oportunidade">
            {(Object.keys(TIPOS) as Tipo[]).map((t) => (
              <option key={t} value={t}>
                {TIPOS[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          Responsável *
          <select value={responsavel} onChange={(e) => setResponsavel(e.target.value)} data-teste="responsavel-oportunidade">
            {!responsavel && <option value="">Escolha o responsável</option>}
            {responsaveis.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome}
              </option>
            ))}
          </select>
        </label>
      </div>
      <fieldset className="form-grade grade-3 bloco-proxima-acao" data-teste="proxima-acao-criacao">
        <legend>
          Próxima ação<Ajuda titulo="Próxima ação">Obrigatória para Oportunidade aberta. Vem preenchida e pode ser alterada.</Ajuda>
        </legend>
        <label className="campo">
          Descrição *
          <input value={acao.descricao} onChange={(e) => setAcao({ ...acao, descricao: e.target.value })} maxLength={200} data-teste="descricao-proxima-acao" />
        </label>
        <label className="campo">
          Data *
          <input type="date" value={acao.data} min={hojeIso()} onChange={(e) => setAcao({ ...acao, data: e.target.value })} data-teste="data-proxima-acao" />
        </label>
        <label className="campo">
          Responsável *
          <select value={acao.responsavel_id || responsavel} onChange={(e) => setAcao({ ...acao, responsavel_id: e.target.value })} data-teste="responsavel-proxima-acao">
            {responsaveis.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome}
              </option>
            ))}
          </select>
        </label>
      </fieldset>
    </Modal>
  );
}
